import { Component, type ReactNode, type ErrorInfo } from "react";
import { isRecoverableChunkError } from "@/lib/chunk-load-error";

interface ErrorBoundaryProps {
  children: ReactNode;
  level?: "root" | "page";
}

interface ErrorBoundaryState {
  hasError: boolean;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(_error: Error): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error(`[ErrorBoundary:${this.props.level || "root"}]`, error, errorInfo);

    // When a lazy-loaded chunk can't be fetched (stale cache after a deploy),
    // a soft reload won't help because the browser would re-serve the same cached
    // index.html. Force a full navigation instead so the server sends fresh HTML.
    if (isRecoverableChunkError(error)) {
      const reloadKey = 'chunk-error-reloads';
      const reloads = parseInt(sessionStorage.getItem(reloadKey) || '0', 10);
      if (reloads < 2) {
        sessionStorage.setItem(reloadKey, String(reloads + 1));
        window.location.href = window.location.href;
        return;
      }
      sessionStorage.removeItem(reloadKey);
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center p-8 bg-background">
          <div className="text-center space-y-4 max-w-md">
            <h2 className="text-lg font-semibold text-foreground">Something went wrong</h2>
            <p className="text-sm text-muted-foreground">
              An unexpected error occurred. Refreshing the page should resolve it.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
            >
              Refresh Page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
